import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

export interface RiskPlayerItem {
  userId: number;
  email: string;
  riskLevel: string;
  kycStatus: string;
}

interface RiskPlayersState {
  list: RiskPlayerItem[];
  isListFetched: boolean;
  isLoading: boolean;
  activePage: number;
  totalPages: number;
  totalItems: number;
}

const initialState: RiskPlayersState = {
  list: [],
  isListFetched: false,
  isLoading: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
};

export const getRiskPlayersList = createAsyncThunk(
  "riskPlayers/list",
  async (
    {
      limit = 20,
      offset = 0,
      email,
      userIds,
      riskStatuses,
      kycStatuses,
    }: {
      limit?: number;
      offset?: number;
      email?: string;
      userIds?: number[];
      riskStatuses?: string[];
      kycStatuses?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await instance.post(apiRoutes.riskPlayersList, {
        limit,
        offset,
        email,
        userIds,
        riskStatuses,
        kycStatuses,
      });

      return result.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const riskPlayersSlice = createSlice({
  name: "riskPlayers",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRiskPlayersList.pending, (state) => {
      state.isLoading = true;
      state.isListFetched = false;
    });

    builder.addCase(getRiskPlayersList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isListFetched = true;

      state.list = action.payload?.items ?? [];

      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
    });

    builder.addCase(getRiskPlayersList.rejected, (state) => {
      state.isLoading = false;
      state.isListFetched = true;
    });
  },
});

export const { handlePaginate } = riskPlayersSlice.actions;
